/**
 * @file Grid Array Editor (horizontal grid layout)
 * @version 1.0.0
 */

import ArrayEditor, {
  ARRAY_ITEM_CONTROLS_TEMPLATE,
  ARRAY_HEADER_TEMPLATE,
} from './ArrayEditor.js';
import CompactPrimitiveEditor from './CompactPrimitiveEditor.js';
import { registerEditor, resolveEditor } from './component-registry.js';
import FilterHeader from './FilterHeader.js';
import SortControl from './SortControl.js';


export const GRID_HEADERS_TEMPLATE = `
  <!-- Sortable headers -->
  <sort-control
    v-if="isSortable"
    :schema="customizedSchema"
    :fields="gridFieldNames"
    :sort="effectiveSort"
    :actionControlStyles="actionControlStyles"
    :fieldHeaderStyles="fieldHeaderStyles"
    @update:sort="handleSortChange"
  />

  <!-- Plain headers when not sortable -->
  <template v-else>
    <div class="fw-bold" :style="actionControlStyles"></div>
    <div
      v-for="fieldName in gridFieldNames"
      :key="'header-' + fieldName"
      class="fw-bold"
      :style="fieldHeaderStyles"
    >
      {{ getFieldTitle(fieldName) }}
    </div>
  </template>

  <!-- Filter header row (per-column filters) -->
  <filter-header
    v-if="showFilterHeader"
    :schema="getItemSchema()"
    :fields="gridFieldNames"
    :query="effectiveQuery"
    :actionControlStyles="actionControlStyles"
    :fieldHeaderStyles="fieldHeaderStyles"
    @update:query="handleQueryChange"
  />
`;

const GridArrayEditor = {
  name: 'GridArrayEditor',
  extends: ArrayEditor,

  setup(props, ctx) {
    return ArrayEditor.setup(props, ctx);
  },

  data() {
    return {
      useFilterHeaderDefault: true,
      useFilterBarDefault: false,
    };
  },

  components: {
    'sort-control': SortControl,
    'filter-header': FilterHeader,
    ...ArrayEditor.components
  },

  methods: {
    /**
     * Override to use inline/compact variants for grid display
     * @param itemSchema
     */
    getItemComponent(itemSchema) {
      // Use explicit editor if specified
      if (itemSchema.editor) {
        return itemSchema.editor;
      }
      // Use CompactPrimitiveEditor for primitives (not in registry)
      if (itemSchema.type !== 'object' && itemSchema.type !== 'array') {
        return CompactPrimitiveEditor;
      }
      // Force inline variant for objects, grid for nested arrays
      const variant = itemSchema.type === 'object' ? 'inline' : 'grid';
      return resolveEditor({ ...itemSchema, variant }, this.mergedDefaults);
    },
  },

  props: {
    vertical: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    /**
     * Get field names for grid headers
     */
    gridFieldNames() {
      if (this.isEmpty) return [];

      const itemSchema = this.getItemSchema();
      if (itemSchema.type !== 'object' || !itemSchema.properties) return [];

      return Object.keys(itemSchema.properties);
    },

    /**
     * Get field title
     */
    getFieldTitle() {
      return fieldName => {
        const itemSchema = this.getItemSchema();
        if (
          itemSchema.type === 'object' &&
          itemSchema.properties?.[fieldName]
        ) {
          return itemSchema.properties[fieldName].title || fieldName;
        }
        return fieldName;
      };
    },

    /**
     * Whether to show filter header row
     */
    showFilterHeader() {
      return (
        this.getAttribute('useFilterHeader', this.useFilterHeaderDefault) &&
        this.filterActive &&
        this.isFilterSupported
      );
    },

    /**
     * Grid layout styles
     */
    gridLayoutStyles() {
      const fieldCount = this.gridFieldNames.length;
      const actionWidth = this.showArrayControls ? 'max-content' : '0';
      const actionHeight = this.showArrayControls ? 'auto' : '0';

      if (this.vertical) {
        return {
          display: 'grid',
          gridTemplateRows: `${actionHeight} repeat(${fieldCount}, auto)`,
          gridTemplateColumns: 'auto',
          gridAutoColumns: 'auto',
          gridAutoFlow: 'column',
          gap: '0.5rem',
          alignItems: 'start',
          overflowX: 'auto',
        };
      }

      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${fieldCount}, auto) ${actionWidth}`,
        gridTemplateRows: 'auto',
        gridAutoColumns: 'auto',
        gridAutoFlow: 'row dense',
        gap: '0.5rem',
        alignItems: 'start',
        overflowX: 'auto',
      };
    },

    /**
     * Action control styles
     */
    actionControlStyles() {
      const baseHidden = { width: '0', height: '0', overflow: 'hidden' };

      if (this.vertical) {
        return this.showArrayControls ? {} : baseHidden;
      }

      return this.showArrayControls
        ? { gridColumn: '-2 / -1' }
        : { ...baseHidden, gridColumn: '-2 / -1' };
    },

    /**
     * Field header styles
     */
    fieldHeaderStyles() {
      if (this.vertical) {
        return {
          borderRight: '1px solid #dee2e6',
          paddingRight: '0.5rem',
          height: '100%',
        };
      }

      return {
        borderBottom: '1px solid #dee2e6',
        paddingBottom: '0.5rem',
      };
    },

    /**
     * Move icon class based on direction
     */
    moveUpIcon() {
      return this.vertical ? 'bi-chevron-left' : 'bi-chevron-up';
    },

    moveDownIcon() {
      return this.vertical ? 'bi-chevron-right' : 'bi-chevron-down';
    },

    /**
     * Move up title based on orientation
     */
    moveUpTitle() {
      return this.vertical ? 'Move left' : 'Move up';
    },

    /**
     * Move down title based on orientation
     */
    moveDownTitle() {
      return this.vertical ? 'Move right' : 'Move down';
    },
  },

  template: `
    <div class="array-editor" v-if="canRead" v-bind="$attrs">
      ${ARRAY_HEADER_TEMPLATE}

      <!-- Items content -->
      <div v-if="!isUndefined" class="array-items">
        <div class="array-grid" :style="gridLayoutStyles" :key="gridFieldNames.length">
          <!-- Grid headers (with optional sort control via slot, includes filter header) -->
          <slot
            name="columns-titles"
            :fields="gridFieldNames"
            :getFieldTitle="getFieldTitle"
            :actionControlStyles="actionControlStyles"
            :fieldHeaderStyles="fieldHeaderStyles"
          >
            ${GRID_HEADERS_TEMPLATE}
          </slot>

          <!-- Grid items -->
          <template v-for="(sourceIndex, displayIndex) in displayIndices" :key="sourceIndex">
            <component
              :is="getItemComponent(getItemSchema())"
              :schema="getItemSchema()"
              :value="getItemValue(displayIndex)"
              :reference="getItemReference(displayIndex)"
              :mode="childMode"
              :deletable="false"
              :displayLabel="false"
              :displayFieldsLabels="false"
              :controlStyles="actionControlStyles"
              :vertical="!vertical"
              @input="(state) => handleItemInput(displayIndex, state)"
            >
              <template #controls>
                <div class="d-flex gap-1 align-items-center">
                  ${ARRAY_ITEM_CONTROLS_TEMPLATE}
                </div>
              </template>
            </component>
          </template>
        </div>

        <!-- Empty state -->
        <div v-if="isEmpty" class="text-muted fst-italic py-1">
          Empty
        </div>

        <!-- Pagination controls -->
        <pagination-controls v-if="isPaginationVisible" />
      </div>
    </div>
  `,
};

registerEditor('array', 'default', GridArrayEditor);
registerEditor('array', 'grid', GridArrayEditor);

export default GridArrayEditor;
